import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TNil } from '@app/app.interface';
import { IApiResponse } from '@core/interfaces/api-response.interface';
import { IGame } from '@core/interfaces/game.interface';
import { GameService } from '@core/services/game.service';
import { ComponentStore } from '@ngrx/component-store';
import { catchError, EMPTY, MonoTypeOperatorFunction, Observable, pipe, switchMap, tap, withLatestFrom } from 'rxjs';

interface IGamePlayState {
  error: string | null;
  games: IGame[];
  gameValidation: IGame | TNil;
  hasMore: boolean;
  limit: number;
  loading: boolean;
  loadingGames: boolean;
  searchTerm: string;
  skip: number;
}

const initialState: IGamePlayState = {
  error: null,
  games: [],
  gameValidation: null,
  hasMore: true,
  limit: 20,
  loading: false,
  loadingGames: false,
  searchTerm: '',
  skip: 0,
};

@Injectable()
export class GamePlayStore extends ComponentStore<IGamePlayState> {
  public readonly games$ = this.selectSignal((state: IGamePlayState) => state.games);
  public readonly loading$ = this.selectSignal((state: IGamePlayState) => state.loading);
  public readonly loadingGames$ = this.selectSignal((state: IGamePlayState) => state.loadingGames);
  public readonly error$ = this.selectSignal((state: IGamePlayState) => state.error);
  public readonly hasMore$ = this.selectSignal((state: IGamePlayState) => state.hasMore);
  public readonly gameValidation$ = this.selectSignal((state: IGamePlayState) => state.gameValidation);
  public readonly loadingMoreGames$ = this.selectSignal((state: IGamePlayState) => state.skip > 0);

  private readonly handleError = (errorMessage: string): MonoTypeOperatorFunction<unknown> =>
    pipe(
      catchError(() => {
        this.patchState({ error: errorMessage, loading: false });

        return EMPTY;
      }),
    );

  public readonly setSearchTerm = this.updater(
    (state: IGamePlayState, searchTerm: string): IGamePlayState => ({
      ...state,
      searchTerm,
    }),
  );

  public readonly resetPagination = this.updater(
    (state: IGamePlayState): IGamePlayState => ({
      ...state,
      games: [],
      hasMore: true,
      skip: 0,
    }),
  );

  public readonly fetchPage = this.effect((params$: Observable<{ reset: boolean }>) => {
    return params$.pipe(
      tap(() => this.patchState({ error: null, loading: true, loadingGames: true })),
      withLatestFrom(this.state$),
      switchMap(([{ reset }, state]) => {
        const { searchTerm, skip, limit } = state;

        let httpParams = new HttpParams()
          .set('limit', String(limit))
          .set('skip', String(skip))
          .set('order_by', '-popularity');

        if (searchTerm && searchTerm.trim().length > 0) {
          httpParams = httpParams.set('acronym__ilike', searchTerm.trim());
        }

        return this.gameService.getGames(httpParams).pipe(
          tap((games: IApiResponse<IGame[]>) => {
            const nextGames = reset ? (games.data ?? []) : [...state.games, ...(games.data ?? [])];
            const nextSkip = (reset ? 0 : state.skip) + (games.data?.length ?? 0);
            const hasMore = games.data?.length === limit;

            this.patchState({ games: nextGames, hasMore, loading: false, loadingGames: false, skip: nextSkip });
          }),
          this.handleError('Failed to load games'),
        );
      }),
    );
  });

  public readonly fetchMore = this.effect((trigger$: Observable<void>) => {
    return trigger$.pipe(
      withLatestFrom(this.state$),
      switchMap(([, state]) => {
        if (state.loading || !state.hasMore) {
          return EMPTY;
        }

        this.fetchPage({ reset: false });

        return EMPTY;
      }),
    );
  });

  public readonly checkGameNameAvailability = this.effect((trigger$: Observable<string>) => {
    return trigger$.pipe(
      tap(() => this.patchState({ loading: true })),
      switchMap((gameName) => {
        return this.gameService.checkGameName(gameName).pipe(
          tap((gameValidation) => this.patchState({ gameValidation, loading: false })),
          catchError(() => {
            this.patchState({
              error: 'Game name not found',
              gameValidation: undefined,
              loading: false,
            });

            return EMPTY;
          }),
        );
      }),
    );
  });

  constructor(private readonly gameService: GameService) {
    super(initialState);
  }
}
